import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import classNames from "classnames";
import type { LightboxExternalProps, Plugin, SlotStyles } from "yet-another-react-lightbox";
import styles from "./index.module.scss";
import backward from "@/assets/backward.svg";
import forward from "@/assets/forward.svg";
import { LayoutStyleTypes } from "@/util-react/layout";

const lightboxStyles: SlotStyles = {
  root: {
    "--yarl__color_backdrop": "#fff",
    "--yarl__container_background_color": "#fff",
    "--yarl__color_button": "#71717a",
    "--yarl__color_button_active": "#52525b",
    "--yarl__color_button_disabled": "#a1a1aa",
    "--yarl__button_filter": "none",
    "--yarl__thumbnails_container_background_color": "#fff",
    "--yarl__thumbnails_thumbnail_background": "#fff",
    "--yarl__thumbnails_thumbnail_icon_color": "#71717a",
    "--yarl__thumbnails_thumbnail_icon_filter": "none",
  },
};

const TAP_MAX_OFFSET_PX = 10;

type Item = { url: string; alt: string; largeUrl?: string; isGenerated: boolean };

interface FixCarouselProps {
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  items: Item[];
}

export const FixCarousel: React.FC<FixCarouselProps> = ({ type = LayoutStyleTypes.TYPE_LG, items }) => {
  const [current, setCurrent] = useState(0);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPkg, setLightboxPkg] = useState<{
    Lightbox: React.ComponentType<LightboxExternalProps>;
    Thumbnails: Plugin;
    Zoom: Plugin;
  } | null>(null);

  const carouselRef = useRef<HTMLDivElement>(null);
  const [imageWidth, setImageWidth] = useState<number>(0);
  const firstMeasured = useRef(false);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const apply = (width: number) => {
      const w = Math.max(256, Math.round(width));
      setImageWidth((previousWidth) => (previousWidth === w ? previousWidth : w));
      firstMeasured.current = true;
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) apply(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      import("yet-another-react-lightbox"),
      import("yet-another-react-lightbox/plugins/thumbnails"),
      import("yet-another-react-lightbox/plugins/zoom"),
      import("yet-another-react-lightbox/styles.css"),
      import("yet-another-react-lightbox/plugins/thumbnails.css"),
    ]).then(([lightboxMod, thumbnailsMod, zoomMod]) => {
      if (cancelled) return;
      setLightboxPkg({
        Lightbox: lightboxMod.Lightbox,
        Thumbnails: thumbnailsMod.default,
        Zoom: zoomMod.default,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const moreOneImages = items.length > 1;
  const navigation = type === LayoutStyleTypes.TYPE_LG;
  const thumbnails = type === LayoutStyleTypes.TYPE_LG;
  const pagination = type === LayoutStyleTypes.TYPE_SM;

  const goToSlide = (idx: number) => setCurrent((idx + items.length) % items.length);
  const handlePrev = () => goToSlide(current - 1);
  const handleNext = () => goToSlide(current + 1);

  const onTouchStart = (e: React.TouchEvent) => {
    setDragStartX(e.touches[0].clientX);
    setIsDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || dragStartX === null) return;
    setDragOffset(e.touches[0].clientX - dragStartX);
  };

  const slideWidth = imageWidth || 256;
  const hasMeasured = firstMeasured.current;

  const finalizePointerGesture = () => {
    if (!isDragging) return;
    const offset = dragOffset;
    const slideThreshold = slideWidth / 3;
    const slideChanged = Math.abs(offset) > slideThreshold;
    if (slideChanged) {
      setCurrent((c) => {
        if (offset < 0) return (c + 1) % items.length;
        return (c - 1 + items.length) % items.length;
      });
    } else if (items.length > 0 && Math.abs(offset) < TAP_MAX_OFFSET_PX) {
      setLightboxOpen(true);
    }
    setDragOffset(0);
    setDragStartX(null);
    setIsDragging(false);
  };

  const onTouchEnd = () => finalizePointerGesture();

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragStartX(e.clientX);
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || dragStartX === null) return;
    setDragOffset(e.clientX - dragStartX);
  };
  const onMouseUp = () => finalizePointerGesture();

  const lightboxSlides = useMemo(
    () =>
      items.map((item) => {
        const fullSize = item.largeUrl ?? item.url;
        return {
          src: fullSize,
          alt: item.alt,
          thumbnail: fullSize,
        };
      }),
    [items],
  );

  const onLightboxView = useCallback(({ index }: { index: number }) => {
    setCurrent(index);
  }, []);

  const LightboxComponent = lightboxPkg?.Lightbox;
  const ThumbnailsPlugin = lightboxPkg?.Thumbnails;
  const ZoomPlugin = lightboxPkg?.Zoom;

  const showLightbox = () => {
    return !(!LightboxComponent || !ThumbnailsPlugin || !ZoomPlugin || items.length === 0 || (items.length === 1 && items[0].isGenerated));
  };

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        {navigation && moreOneImages && (
          <button className={classNames(styles.navButton, styles.prev)} onClick={handlePrev} aria-label="Previous" type="button">
            <img src={backward} alt="Previous" />
          </button>
        )}

        <div
          className={styles.carousel}
          ref={carouselRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          style={{ cursor: showLightbox() ? "zoom-in" : isDragging ? "grabbing" : "grab" }}
        >
          <div
            className={styles.imagesWrapper}
            style={{
              transform: `translate3d(${-current * slideWidth + dragOffset}px, 0, 0)`,
              transition: isDragging || !hasMeasured ? "none" : "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {items.map((item, idx) => (
              <div
                className={styles["image-container"]}
                key={idx}
                style={{
                  width: `${slideWidth}px`,
                  minWidth: `${slideWidth}px`,
                  flex: `0 0 ${slideWidth}px`,
                }}
              >
                <img
                  src={item.url}
                  alt={item.alt}
                  className={styles.image}
                  width={256}
                  height={256}
                  loading={idx === 0 ? "eager" : "lazy"}
                  fetchpriority={idx === 0 ? "high" : undefined}
                  decoding="async"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>

        {navigation && moreOneImages && (
          <button className={classNames(styles.navButton, styles.next)} onClick={handleNext} aria-label="Next" type="button">
            <img src={forward} alt="Next" />
          </button>
        )}
      </div>

      {pagination && moreOneImages && (
        <div className={styles.pagination}>
          {items.map((_, idx) => (
            <button
              key={idx}
              className={classNames(styles.paginationItem, { [styles.active]: idx === current })}
              onClick={() => goToSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              type="button"
            />
          ))}
        </div>
      )}

      {thumbnails && moreOneImages && (
        <div className={styles.thumbnails}>
          <div className={styles["thumbnails-list"]}>
            {items.map((item, idx) => (
              <div
                key={idx}
                className={classNames(styles.thumbnail, { [styles.active]: idx === current })}
                onClick={() => goToSlide(idx)}
                tabIndex={0}
                role="button"
                aria-label={`Thumbnail ${idx + 1}`}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") goToSlide(idx);
                }}
              >
                <img alt="thumbnail" src={item.url} />
              </div>
            ))}
          </div>
        </div>
      )}

      {showLightbox() && (
        <LightboxComponent
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={current}
          slides={lightboxSlides}
          styles={lightboxStyles}
          plugins={moreOneImages ? [ThumbnailsPlugin, ZoomPlugin] : [ZoomPlugin]}
          {...(moreOneImages ? { thumbnails: { position: "bottom" as const } } : {})}
          {...(!moreOneImages ? { render: { buttonPrev: () => null, buttonNext: () => null } } : {})}
          zoom={{ pinchZoomV4: true, maxZoomPixelRatio: 4 }}
          controller={{
            closeOnBackdropClick: true,
            ...(!moreOneImages ? { disableSwipeNavigation: true } : {}),
          }}
          on={{ view: onLightboxView }}
        />
      )}
    </div>
  );
};
